import React, { useEffect, useState } from 'react';
import DataTableComponent, { DataTableProps } from './DataTableComponent';
import LoadingContent from './LoadingContent';
import { EntryTableBasicProps } from './EntriesTableWithQueryParams';
import { useEntryQuery, useUpdateEntryMutation } from '../services/entries/entries.service';
import { IEntry, IEntryProperty } from '../common/interfaces/entry.interface';
import PageHeader from './PageHeader';
import PageLayout from '../layouts/PageLayout';
import { useNavigate, useParams } from 'react-router-dom';
import Input from './Input';
import Button from './Button';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  PlusIcon,
} from '@heroicons/react/20/solid';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { classNames } from '../common/utils/utils';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from './ConfirmationModal';
import { useErrorToast, useSuccessToast } from '../hooks/useToast';
import FormTextarea from './FormTextarea';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useClientQuery } from '../services/clients/client.service';

export const TOTAL_VENIT_LUNAR_REALIZAT = 'Total venit lunar realizat';

// Clear and remove TOTAL_VENIT_LUNAR_REALIZAT
const clearPropertiesValues = (properties: IEntryProperty[]) => {
  const result = properties
    .map((el) => {
      if (el.key !== TOTAL_VENIT_LUNAR_REALIZAT) {
        return { ...el, value: undefined };
      }
    })
    .filter(Boolean);
  return result;
};

const calculateInitialProperties = (properties?: IEntryProperty[]) => {
  return {
    properties: [
      ...(properties ? clearPropertiesValues(properties) : []),
      { id: '0', key: TOTAL_VENIT_LUNAR_REALIZAT, value: '0' },
    ],
  };
};

type EntryTableType<T extends object> = React.FC<EntryTableBasicProps> & {
  DataTableComponent: React.FC<DataTableProps<T>>;
};

const EntryTable: EntryTableType<IEntry> = () => {
  const [pendingData, setPendingData] = useState<IEntry>();
  const [isConfirmationSaveOpen, setIsConfirmationSaveOpen] = useState(false);
  const { requestId, year, month, clientId, companyId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('entry_table');
  const [valuesSum, setValuesSum] = useState<number>(0);

  const { handleSubmit, control, reset } = useForm<IEntry>({
    defaultValues: { properties: [{ id: '0', key: TOTAL_VENIT_LUNAR_REALIZAT, value: '0' }] },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { fields, insert } = useFieldArray<IEntry>({
    control,
    name: 'properties',
  });

  const {
    data: entryReponse,
    isLoading: isEntryLoading,
    error: entryError,
    refetch,
  } = useEntryQuery(requestId || '', year || '', month || '');

  const { data: client, isLoading: isClientLoading } = useClientQuery(clientId);

  useEffect(() => {
    if (entryError) {
      useErrorToast('Eroare la preluarea datelor.');
      navigate('/');
    }
  }, [entryError]);

  useEffect(() => {
    if (!entryReponse?.entry?.id) {
      reset(calculateInitialProperties(entryReponse?.entry?.properties));
      setValuesSum(0);
    } else {
      reset(entryReponse?.entry);
      setValuesSum(0);
    }
  }, [entryReponse]);

  const { mutateAsync: addEntry, error: addEntryError } = useUpdateEntryMutation();

  useEffect(() => {
    if (addEntryError) {
      useErrorToast('Eroare la salvarea datelor.');
    }
  }, [addEntryError]);

  const onAddNewColumn = () => {
    insert(fields.length - 1, { id: `${fields.length}`, key: '', value: '' });
  };

  const onVerify = async (data: IEntry) => {
    if (year && month && requestId && data) {
      const sum = data.properties.reduce((acc, curr) => (acc += +curr.value), 0);
      const total = +(
        data?.properties.find((el) => el.key === TOTAL_VENIT_LUNAR_REALIZAT)?.value || 0
      );
      setValuesSum(sum - total);
    }
  };

  const onSave = async (data: IEntry) => {
    if (year && month && requestId && data) {
      const sum = data.properties.reduce((acc, curr) => (acc += +curr.value), 0);
      setValuesSum(sum);
      const total = +(
        data?.properties.find((el) => el.key === TOTAL_VENIT_LUNAR_REALIZAT)?.value || 0
      );

      if (sum - total !== total) {
        setPendingData(data);
        setIsConfirmationSaveOpen(true);
        return;
      }

      saveEntry(data);
    }
  };
  const saveEntry = async (data?: IEntry) => {
    if (year && month && requestId && data) {
      const properties = data.properties.filter((el: IEntryProperty) => el.key !== '');
      await addEntry(
        {
          entry_id: entryReponse?.entry.id,
          year: +year,
          month: +month,
          properties,
          request_id: +requestId,
          details: data.details,
        },
        {
          onSuccess: () => {
            useSuccessToast(t('success'));
            refetch();
          },
          onSettled: () => {
            setPendingData(undefined);
          },
        },
      );
    }
  };

  const navigatePreviousMonth = () => {
    if (!year || !month) {
      return;
    }

    const monthValue = +month;
    const yearValue = +year;

    let route = `/clients/${clientId}/requests/${requestId}`;

    if (companyId) {
      route = `/companies/${companyId}${route}`;
    }

    if (monthValue - 1 == 0) {
      navigate(`${route}/${yearValue - 1}/${12}`);
      return;
    } else {
      navigate(`${route}/${year}/${monthValue - 1}`);
    }
  };

  const navigateNextMonth = () => {
    if (!year || !month) {
      return;
    }

    const monthValue = +month;
    const yearValue = +year;

    let route = `/clients/${clientId}/requests/${requestId}`;

    if (companyId) {
      route = `/companies/${companyId}${route}`;
    }

    if (monthValue + 1 > 12) {
      navigate(`${route}/${yearValue + 1}/${1}`);
      return;
    } else {
      navigate(`${route}/${yearValue}/${monthValue + 1}`);
    }
  };
  const onConfirmationSaveClose = () => {
    setIsConfirmationSaveOpen(false);
    setPendingData(undefined);
  };

  if (isEntryLoading) {
    return <LoadingContent />;
  }

  if (!entryReponse?.date_in_range) {
    if (!entryReponse?.request) {
      navigate('/');
    } else {
      const year = (entryReponse?.request?.start_date as string).split('-')[0];
      const month = (entryReponse?.request?.start_date as string).split('-')[1];
      navigate(`/clients/${clientId}/requests/${requestId}/${year}/${month}`);
    }
  }

  return (
    <PageLayout>
      <div className="flex gap-2 items-center flex-col">
        <div className="flex items-start w-full">
          <p className="text-md p-0 m-0">{`Client: ${isClientLoading ? '...' : client?.name}`}</p>
        </div>

        <div className="flex gap-4 justify-between w-full">
          <PageHeader
            title={entryReponse?.request?.name || 'Data Not Found'}
            buttonId="client__return-button"
          />
          <div className="flex gap-2 ">
            <Button
              id={'previous-month'}
              label={''}
              className="border-solid mt-1 border-blue border-1 rounded hover:bg-blue"
              icon={
                <ChevronLeftIcon className="h-8 text-blue hover:text-white" aria-hidden="true" />
              }
              type="button"
              onClick={() => navigatePreviousMonth()}
            />
            <h1 className="select-none">
              {year} / {month}
            </h1>
            <Button
              id={'previous-month'}
              label={''}
              className="border-solid mt-1 border-blue border-1 rounded hover:bg-blue"
              icon={
                <ChevronRightIcon className="h-8 text-blue hover:text-white" aria-hidden="true" />
              }
              type="button"
              onClick={() => navigateNextMonth()}
            />
          </div>
          <div className="flex gap-2">
            <Button
              label={t('save_button')}
              className="btn-primary"
              onClick={handleSubmit(onSave)}
            />
          </div>
        </div>
      </div>
      <div className="grid gap-4 w-full overflow-x-scroll no-scrollbar">
        <p className="text-xl">{t('title')}</p>

        <div className="grid grid-flow-col auto-cols-min gap-4">
          {fields?.map((field: IEntryProperty, index: number) => {
            return (
              <div className="w-[12rem]" key={`${field.id}_column`}>
                <Controller
                  name={`properties.${index}.key`}
                  control={control}
                  key={`${field.id}_column`}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      type="text"
                      key={field.id}
                      placeholder={t('new_column')}
                      onChange={onChange}
                      value={value}
                      disabled={field.key === TOTAL_VENIT_LUNAR_REALIZAT}
                    />
                  )}
                />
              </div>
            );
          })}
          <div className="w-[10rem] flex gap-2">
            <Button
              className="btn-primary h-10 mb-[0.1rem]"
              type="button"
              onClick={onAddNewColumn}
              label=""
              icon={<PlusIcon className="h-5 w-5" />}
            />
          </div>
        </div>
        <hr />
        <div className={classNames(`grid grid-flow-col auto-cols-min gap-4`)}>
          {fields?.map((field: IEntryProperty, index: number) => (
            <div className="w-[12rem]" key={`${field.id}_value`}>
              <Controller
                name={`properties.${index}.value`}
                key={`${field.id}_value`}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    type="number"
                    key={field.id}
                    placeholder={t('new_value')}
                    onChange={onChange}
                    value={value}
                  />
                )}
              />
            </div>
          ))}
          <div className="w-[10rem] flex gap-2"></div>
        </div>
      </div>

      <div className="flex flex-col">
        <p className="text-sm  text-gray-400">{t('helper')}</p>
        <div className="flex gap-2 items-center">
          <p className="text-sm">
            Suma valorilor coloanelor adaugate este: <span className="text-lg">{valuesSum}</span>{' '}
          </p>
          <Button
            label={'Calculeaza suma'}
            icon={<ArrowPathIcon className="w-5 h-5" />}
            className="btn-outline-secondary p-1 pr-2"
            onClick={handleSubmit(onVerify)}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 w-full">
        {entryReponse?.request?.details && (
          <div className="flex gap-4 flex-col">
            <PageHeader
              title={`${entryReponse?.request?.name} - ${t('employee_details')}`}
              buttonId="client__return-button"
            />
            <div>
              <pre>{entryReponse?.request?.details}</pre>
            </div>
          </div>
        )}

        <div className="w-[30rem]">
          <PageHeader title={`Observatii stat de plata`} buttonId="client__return-button" />
          <Controller
            key="details"
            name="details"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <FormTextarea readOnly={false} value={value || ''} label={``} onChange={onChange} />
              );
            }}
          />
        </div>
      </div>
      {isConfirmationSaveOpen && (
        <ConfirmationModal
          onConfirm={() => {
            setIsConfirmationSaveOpen(false);
            saveEntry(pendingData);
          }}
          onClose={onConfirmationSaveClose}
          icon={<ExclamationCircleIcon className="text-yellow h-16 w-14" />}
          confirmButtonClass="btn-primary"
          title={`Suma coloanelor nu este egala cu valoarea coloanei ${TOTAL_VENIT_LUNAR_REALIZAT}. Esti sigur ca vrei sa continui?`}
          confirmButtonLabel={'Salveaza'}
        />
      )}
    </PageLayout>
  );
};

EntryTable.DataTableComponent = DataTableComponent;

export default EntryTable;
